import { Component, Input, OnChanges, OnInit, TemplateRef, input, viewChild, inject } from '@angular/core';
import { Form } from '../../data/form';
import { Process } from '../../data/process';
import { FormService } from '../../services/form.service';
import {
  BrowserMessage,
  BrowserMessagesService,
  BrowserMessageType, CopyTooltipDirective, LoaderComponent,
  ModalRef,
  ModalService, MultiSelectComponent,
  Step, StepsComponent
} from '@rbb/ng-web-template-lib';
import { WebAuthService } from '@rbb/ng-web-auth-lib';
import { ProcessStatus } from '../../types/process-status';
import { Employee } from '../../data/employee';
import { EmployeeService } from '../../services/employee.service';
import { ProcessField } from '../../data/process-field';
import { FieldType } from '../../types/field-type';
import { ProcessService } from '../../services/process.service';
import { BodyProcess } from '../../body/body-process';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { FieldService } from '../../services/field.service';
import { TelefonbuchData } from '../../data/telefonbuch-data';
import { EmployeeType } from '../../types/employee-type';
import { CostCentre } from '../../data/cost-centre';
import { environment } from '../../../environments/environment';
import { BodyContent } from '../../body/body-content';
import { AccessService } from '../../services/access.service';
import { UtilService } from '../../services/util.service';
import { BodyProcessField } from '../../body/body-process-field';
import { Observable } from 'rxjs';
import { Person } from '../../data/person';
import { cloneDeep, isEqual, uniq } from 'lodash';
import { Field } from '../../data/field';
import { EmployeeOrgUnit } from '../../data/employee-org-unit';
import { OrgUnitType } from '../../types/org-unit-type';
import { EmployeeOrgUnitService } from '../../services/employee-org-unit.service';
import { ConditionalType } from '../../types/conditional-type';
import { ApproverListUserViewComponent } from '../approver-list-user-view/approver-list-user-view.component';
import { FormsModule } from '@angular/forms';
import { NgClass, TitleCasePipe } from '@angular/common';
import { PrivacyPolicyComponent } from '../privacy-policy/privacy-policy.component';
import { ProcessPreviewComponent } from '../process-preview/process-preview.component';

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.css'],
  imports: [RouterLink, ApproverListUserViewComponent, FormsModule, NgClass, PrivacyPolicyComponent, ProcessPreviewComponent, TitleCasePipe, LoaderComponent, CopyTooltipDirective, MultiSelectComponent, StepsComponent]
})
export class FormComponent implements OnInit, OnChanges {
  private formService = inject(FormService);
  private employeeService = inject(EmployeeService);
  private employeeOrgUnitService = inject(EmployeeOrgUnitService);
  private processService = inject(ProcessService);
  private fieldService = inject(FieldService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private browserMessagesService = inject(BrowserMessagesService);
  webAuthService = inject(WebAuthService);
  private modalService = inject(ModalService);
  accessService = inject(AccessService);
  private utilService = inject(UtilService);

  Number = Number;

  webUrl = environment.webUrl;

  @Input() form: Form | undefined;
  readonly lastApproved = input<Form>();
  readonly existingProcess = input<Process>();
  process: Process | undefined;
  @Input() isPreview: boolean | undefined;

  params: ParamMap | undefined;
  draftUrl: string | undefined;

  employee: Employee | undefined;
  pendingEmployeeOrgUnits: EmployeeOrgUnit[] | undefined;
  checkAll = false;
  checkPrivacy = false;
  id: string | undefined;
  fillId: string | undefined;

  approvalsValid = true;

  currentStep = 1;
  clientList: string[] = [];
  rooms: { value: string }[] = [];
  roomsLoaded = false;
  costCentres: CostCentre[] = [];
  costCentreLoaded = false;
  groups = new Map<FieldType, string[]>();
  groupsLoaded = false;
  groupsLoading = 0;
  groupSuggestions: Record<FieldType, string[] | undefined> | undefined;
  productionUnits: { Bezeichnung: string; SYSID: string }[] = [];
  myRooms: string[] | undefined;
  myRoomsLoaded = false;
  telefonbuchData: TelefonbuchData | undefined;
  telefonbuchDataLoaded = false;
  approvalsLoading = false;

  steps: Step[] = [
    { name: 'Ausfüllen', func: (): void => this.changeCurrentStep(1) },
    { name: 'Datenschutzerklärung', func: (): void => this.changeCurrentStep(2), disabled: true },
    { name: 'Bestätigen', func: (): void => this.changeCurrentStep(3), disabled: true }
  ];

  contentSelect = Object.create({});
  otherInputs = Object.create({});
  otherChecked = Object.create({});

  readonly groupModal = viewChild<TemplateRef<unknown>>('groupModal');
  selectedGroupField: ProcessField | undefined;
  groupSearch = '';

  readonly ngFileModal = viewChild<TemplateRef<unknown>>('ngFileModal');
  ngFileModalService: ModalRef<unknown> | undefined;
  fileField: ProcessField | undefined;
  checking: boolean | undefined;
  checkingError: string | null | undefined;
  fileContent: { name?: string; mimeType?: string; data?: string } | undefined;

  readonly ngSaveModal = viewChild<TemplateRef<unknown>>('ngSaveModal');
  ngSaveModalService: ModalRef<unknown> | undefined;
  saving = false;
  savingError: string | null | undefined = '';

  ngOnChanges(): void {
    if (this.isPreview) {
      this.employee = undefined;
      this.ngOnInit();
    }
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.params['id'];
    this.fillId = this.route.snapshot.queryParams['fill'];
    this.params = this.route.snapshot.queryParamMap;
    this.getEmployeeOrgUnits();
    this.getClients();
    this.getSpeedikonRooms();
    this.getCostCentres();
    this.getGroups();
    this.getProductionUnits();
    this.checkAcceptedPrivacy();
    this.process = new Process();
    this.process.id = this.existingProcess()?.id;
    this.process.firstName = this.accessService.usernameContext ? this.accessService.usernameContextFirstName : this.webAuthService.authUser?.firstName;
    this.process.lastName = this.accessService.usernameContext ? this.accessService.usernameContextLastName : this.webAuthService.authUser?.lastName;
    this.process.username = this.accessService.usernameContext ?? this.webAuthService.authUser?.username;
    this.process.status = ProcessStatus.DRAFT;
    this.process.formId = this.form?.id;
    this.process.fields = [];
    this.process.approvals = [];
    this.getMySpeedikonRooms();
    this.getMyTelefonbuchData();
  }

  getEmployeeOrgUnits(): void {
    this.employeeOrgUnitService.getOrgUnits().subscribe(data => this.pendingEmployeeOrgUnits = data.filter(employeeOrgUnit => employeeOrgUnit.approved === 'PENDING'));
  }

  fillGetParameter(): void {
    if (this.params && this.process?.fields) {
      const filledFields = [] as string[];
      for (const p of this.params.keys) {
        for (const v of this.params.getAll(p)) {
          for (const f of this.process.fields) {
            if (f.formField?.id && !filledFields.includes(f.formField.id) && f.formField.name === p) {
              f.content = v;
              filledFields.push(f.formField.id);
              this.prepareFilledField(f);
              break;
            }
          }
        }
      }
      this.getMySpeedikonRooms();
      this.getMyTelefonbuchData();
      this.checkAllFields();
      for (const field of this.process.fields) {
        if (field.check) {
          // eslint-disable-next-line no-console
          console.log(field.check);
          this.setDefaultFieldContent(field);
        }
      }
      this.checkAllFields();
    }
  }

  changeCurrentStep(stepNumber: number): void {
    this.currentStep = stepNumber;
    window.scrollTo(0, 0);
  }

  addCustomFieldListItem(field: ProcessField): void {
    if (field.formField) {
      if (!field.formField.fieldCustomParameter.max || field.content.length < field.formField.fieldCustomParameter.max) {
        field.content.push('');
      }
    }
  }

  removeCustomFieldListItem(field: ProcessField): void {
    if (field.formField) {
      if (field.content.length > 1 && (!field.formField.fieldCustomParameter.min || field.content.length > field.formField.fieldCustomParameter.min)) {
        field.content.pop();
      }
    }
  }

  addCustomFieldTableRow(field: ProcessField): void {
    if (field.formField) {
      if (!field.formField.fieldCustomParameter.max || field.content.length < field.formField.fieldCustomParameter.max + (field.formField.fieldCustomParameter.sum ? 2 : 1)) {
        if (field.formField.fieldCustomParameter.sum) {
          field.content.splice(field.content.length - 1, 0, []);
        } else {
          field.content.push([]);
        }
        // eslint-disable-next-line no-empty-pattern
        for (const {} of field.formField.fieldCustomParameter.head) {
          field.content[field.content.length - (field.formField.fieldCustomParameter.sum ? 2 : 1)].push('');
        }
      }
    }
  }

  removeCustomFieldTableRow(field: ProcessField): void {
    if (field.formField) {
      if (field.content.length > (field.formField.fieldCustomParameter.sum ? 2 : 1) && (!field.formField.fieldCustomParameter.min || field.content.length > field.formField.fieldCustomParameter.min + (field.formField.fieldCustomParameter.sum ? 2 : 1))) {
        if (field.formField.fieldCustomParameter.sum) {
          field.content.splice(field.content.length - 2, 1);
        } else {
          field.content.pop();
        }
      }
    }
  }

  addCustomFieldPeriod(field: ProcessField): void {
    if (field.formField) {
      if (!field.formField.fieldCustomParameter.max || field.content.length < field.formField.fieldCustomParameter.max + (field.formField.fieldCustomParameter.sum ? 2 : 1)) {
        field.content.push('');
        field.formField.fieldCustomParameter.fromDate.push('');
        field.formField.fieldCustomParameter.toDate.push('');
      }
    }
  }

  removeCustomFieldPeriod(field: ProcessField): void {
    if (field.formField) {
      if (field.content.length > 1 && (!field.formField.fieldCustomParameter.min || field.content.length > field.formField.fieldCustomParameter.min + 1)) {
        field.content.pop();
        field.formField.fieldCustomParameter.fromDate.pop();
        field.formField.fieldCustomParameter.toDate.pop();
      }
    }
  }

  fill(): void {
    this.processService.getProcess(this.route.snapshot.queryParams['fill']).subscribe({
      next: (next) => {
        this.restoreFromExistingProcess(next);
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Antrag konnte nicht geladen werden');
      }
    });
  }

  restoreFromExistingProcess(p: Process): void {
    if (this.process?.fields && p.fields) {
      const usedFillField = [] as string[];
      for (const field of this.process.fields) {
        for (const fillField of p.fields) {
          if (fillField.id && !usedFillField.includes(fillField.id) && fillField.formField?.name && field.formField?.name
            && fillField.formField.name === field.formField.name && fillField.formField.type === field.formField.type) {
            field.content = fillField.content;
            usedFillField.push(fillField.id);
            this.prepareFilledField(field);
            break;
          }
        }
      }
      this.getMySpeedikonRooms();
      this.getMyTelefonbuchData();
      this.checkAllFields();
      for (const field of this.process.fields) {
        if (field.check) {
          // eslint-disable-next-line no-console
          console.log(field.check);
          this.setDefaultFieldContent(field);
        }
      }
      this.checkAllFields();
    }
  }

  prepareFilledField(field: ProcessField): void {
    if (field.formField) {
      if (field.formField.type === 'PERIOD') {
        if (field.content) {
          const parts = field.content.split(' ');
          if (parts.length === 4) {
            const fromParts = parts[1].split('.');
            field.formField.fieldCustomParameter.fromDate = Number(fromParts[2]).toString().padStart(4, '0') + '-' + Number(fromParts[1]).toString().padStart(2, '0') + '-' + Number(fromParts[0]).toString().padStart(2, '0');
            if (parts[3] === 'unbefristet') {
              field.formField.fieldCustomParameter.toDate = 'unbefristet';
            } else {
              const toParts = parts[3].split('.');
              field.formField.fieldCustomParameter.toDate = Number(toParts[2]).toString().padStart(4, '0') + '-' + Number(toParts[1]).toString().padStart(2, '0') + '-' + Number(toParts[0]).toString().padStart(2, '0');
            }
          }
        }
      }
      if (field.formField.type === 'PERIODS') {
        field.formField.fieldCustomParameter.fromDate = [];
        field.formField.fieldCustomParameter.toDate = [];
        if (field.content) {
          field.content = JSON.parse(field.content);
          for (let i = 0; i < field.content.length; i++) {
            field.formField.fieldCustomParameter.fromDate.push('');
            field.formField.fieldCustomParameter.toDate.push('');
            const parts = field.content[i].split(' ');
            if (parts.length === 7) {
              const fromParts = parts[1].split('.');
              if (fromParts.length === 3) {
                field.formField.fieldCustomParameter.fromDate[i] = Number(fromParts[2]).toString().padStart(4, '0') + '-' + Number(fromParts[1]).toString().padStart(2, '0') + '-' + Number(fromParts[0]).toString().padStart(2, '0');
              }
              const toParts = parts[3].split('.');
              if (toParts.length === 3) {
                field.formField.fieldCustomParameter.toDate[i] = Number(toParts[2]).toString().padStart(4, '0') + '-' + Number(toParts[1]).toString().padStart(2, '0') + '-' + Number(toParts[0]).toString().padStart(2, '0');
              }
            }
          }
        }
      }
      if (field.formField.type === 'PERCENT') {
        if (field.content) {
          const parts = field.content.replace('von ' + field.formField.fieldCustomParameter.absoluteHundred.toString().replace('.', ',') + (field.formField.fieldCustomParameter.suffix ?? '') + ' entspricht ', '')
            .replace('%', '')
            .split(' ');
          if (parts.length === 2) {
            if (field.formField.fieldCustomParameter.timeFormat) {
              field.formField.fieldCustomParameter.absolute = parts[0];
            } else {
              field.formField.fieldCustomParameter.absolute = Number(parts[0].replace(',', '.'));
            }
            field.formField.fieldCustomParameter.percent = Number(parts[1].replace(',', '.'));
          }
        }
      }
      if (field.formField.type === 'COST_CENTRE') {
        if (field.content) {
          const costCentre = this.costCentres.find(item => item.costCentre === field.content);
          if (costCentre) {
            field.formField.fieldCustomParameter.select = [costCentre];
          } else {
            this.setDefaultFieldContent(field);
          }
        }
      }
      if (field.formField.type === 'ROOM') {
        if (field.content) {
          const room = this.rooms.find(item => item === field.content);
          if (room) {
            field.formField.fieldCustomParameter.select = [room];
          } else {
            this.setDefaultFieldContent(field);
          }
        }
      }
      if (field.formField.type === 'PERSON') {
        if (field.content) {
          const lines = field.content.split('\n');
          if (lines.length === 5) {
            const person: {
              firstName?: string;
              lastName?: string;
              username?: string;
              emailAddress?: string;
              uidNumber?: string;
            } = {};
            person.firstName = lines[0].split(': ')[1];
            person.lastName = lines[1].split(': ')[1];
            person.username = lines[2].split(': ')[1];
            person.emailAddress = lines[3].split(': ')[1];
            person.uidNumber = lines[4].split(': ')[1];
            field.formField.fieldCustomParameter.select = [person];
            this.searchPerson(person.firstName + ' ' + person.lastName, field.formField.id ?? '').subscribe({
              next: (data) => {
                let valid = false;
                for (const p of data) {
                  if (isEqual(person, p)) {
                    valid = true;
                    break;
                  }
                }
                if (!valid) {
                  this.setDefaultFieldContent(field);
                }
              },
              error: () => {
                this.setDefaultFieldContent(field);
              }
            });
          }
        }
      }
      if (field.formField.type === 'ACCOUNT') {
        if (field.content) {
          const usernames = field.content.split('\n').map((s: string) => s.split(' - ')[0]) as string[];
          field.formField.fieldCustomParameter.select = [field.content];
          this.searchAccount(usernames.join(';'), field.formField.id ?? '').subscribe({
            next: (data) => {
              const accounts = data.filter((item: string) => usernames.includes(item.split(' - ')[0]));
              if (accounts.length === usernames.length) {
                field.content = accounts.join('\n');
              } else {
                this.setDefaultFieldContent(field);
              }
            },
            error: () => {
              this.setDefaultFieldContent(field);
            }
          });
        }
      }
      if (field.formField.type === 'PERSON_LIST') {
        if (field.content) {
          field.content = JSON.parse(field.content);
          const list = [] as {
            firstName?: string;
            lastName?: string;
            username?: string;
            emailAddress?: string;
            uidNumber?: string;
          }[];
          for (const personString of field.content) {
            const lines = personString.split('\n');
            if (lines.length === 5) {
              const personObject: {
                firstName?: string;
                lastName?: string;
                username?: string;
                emailAddress?: string;
                uidNumber?: string;
              } = {};
              personObject.firstName = lines[0].split(': ')[1];
              personObject.lastName = lines[1].split(': ')[1];
              personObject.username = lines[2].split(': ')[1];
              personObject.emailAddress = lines[3].split(': ')[1];
              personObject.uidNumber = lines[4].split(': ')[1];
              list.push(personObject);
              this.searchPerson(personObject.firstName + ' ' + personObject.lastName, field.formField.id ?? '').subscribe({
                next: (data) => {
                  let valid = false;
                  for (const p of data) {
                    if (isEqual(personObject, p)) {
                      valid = true;
                      break;
                    }
                  }
                  if (!valid) {
                    list.splice(list.indexOf(personObject), 1);
                    field.content.splice(field.content.indexOf(personString), 1);
                    if (!list.length) {
                      this.setDefaultFieldContent(field);
                    }
                  }
                },
                error: () => {
                  list.splice(list.indexOf(personObject), 1);
                  field.content.splice(field.content.indexOf(personString), 1);
                  if (!list.length) {
                    this.setDefaultFieldContent(field);
                  }
                }
              });
            }
          }
          field.formField.fieldCustomParameter.select = list;
        }
      }
      if (field.formField.type === 'PRODUCTION_UNIT') {
        if (field.content) {
          field.content = JSON.parse(field.content);
          const list = [] as {
            Bezeichnung?: string;
            SYSID?: string;
          }[];
          for (const productionUnitString of field.content) {
            const lines = productionUnitString.split('\n');
            if (lines.length === 2) {
              const productionUnitObject: {
                Bezeichnung?: string;
                SYSID?: string;
              } = {};
              productionUnitObject.Bezeichnung = lines[0].split(': ')[1];
              productionUnitObject.SYSID = lines[1].split(': ')[1];
              list.push(productionUnitObject);
              this.fieldService.getProductionUnits().subscribe({
                next: (data) => {
                  let valid = false;
                  for (const p of data) {
                    if (isEqual(productionUnitObject, p)) {
                      valid = true;
                      break;
                    }
                  }
                  if (!valid) {
                    list.splice(list.indexOf(productionUnitObject), 1);
                    field.content.splice(field.content.indexOf(productionUnitString), 1);
                    if (!list.length) {
                      this.setDefaultFieldContent(field);
                    }
                  }
                },
                error: () => {
                  list.splice(list.indexOf(productionUnitObject), 1);
                  field.content.splice(field.content.indexOf(productionUnitString), 1);
                  if (!list.length) {
                    this.setDefaultFieldContent(field);
                  }
                }
              });
            }
          }
          field.formField.fieldCustomParameter.select = list;
        }
      }
      if (field.formField.type === 'SELECTION' || field.formField.type === 'SELECTION_PLUS' || field.formField.type === 'APPLICATION' || field.formField.type === 'BIMEDIAL' || field.formField.type === 'CAD' || field.formField.type === 'CONFLUENCE' || field.formField.type === 'FILESERVICE' || field.formField.type === 'GENERAL' || field.formField.type === 'IT' || field.formField.type === 'JIRA' || field.formField.type === 'MCLOUD' || field.formField.type === 'MUSIC_MASTER' || field.formField.type === 'PROJECT' || field.formField.type === 'RABBIT' || field.formField.type === 'LIST' || field.formField.type === 'FILE' || field.formField.type === 'TABLE' || field.formField.type === 'SELECTION_TABLE') {
        field.content = JSON.parse(field.content);
        if (field.formField.type === 'SELECTION') {
          this.otherInputs[field.id ?? ''] = '';
          this.contentSelect[field.id ?? ''] = field.formField.fieldCustomParameter.items.length > 10 ? field.content[0] : null;
          for (const s of field.content) {
            if (!field.formField.fieldCustomParameter.items.includes(s)) {
              this.otherChecked[field.id ?? ''] = true;
              this.otherInputs[field.id ?? ''] = s.replace(/^(Sonstiges: )/, '');
              break;
            }
          }
        }
        if (field.formField.type === 'SELECTION_PLUS') {
          if (!field.formField.fieldCustomParameter.additional) {
            field.formField.fieldCustomParameter.additional = {};
          }
          for (const item of field.formField.fieldCustomParameter.items) {
            field.formField.fieldCustomParameter.additional[item] = '';
          }
          const suffix = field.formField.fieldCustomParameter.suffix ?? '';
          for (const s of field.content) {
            const parts = s.split(/( -> )/, 2);
            field.formField.fieldCustomParameter.additional[parts[0]] = s.replace(parts[0] + ' -> ', '').replace(new RegExp(suffix + '$'), '');
          }
        }
        if (field.formField.type === 'APPLICATION' || field.formField.type === 'BIMEDIAL' || field.formField.type === 'CAD' || field.formField.type === 'CONFLUENCE' || field.formField.type === 'FILESERVICE' || field.formField.type === 'GENERAL' || field.formField.type === 'IT' || field.formField.type === 'JIRA' || field.formField.type === 'MCLOUD' || field.formField.type === 'MUSIC_MASTER' || field.formField.type === 'PROJECT' || field.formField.type === 'RABBIT') {
          if (!field.formField.fieldCustomParameter.additional) {
            field.formField.fieldCustomParameter.additional = {};
          }
          for (const item of (this.groups.get(field.formField.type) ?? [])) {
            field.formField.fieldCustomParameter.additional[item] = '';
          }
          for (const s of field.content) {
            const parts = s.split(/( -> )/, 2);
            field.formField.fieldCustomParameter.additional[parts[0]] = s.replace(parts[0] + ' -> ', '');
          }
        }
      }
    }
  }

  getGroups(): void {
    if (this.formHasType(FieldType.APPLICATION)) {
      this.groupsLoading++;
      this.fieldService.getApplicationGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.APPLICATION, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.BIMEDIAL)) {
      this.groupsLoading++;
      this.fieldService.getBimedialGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.BIMEDIAL, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.CAD)) {
      this.groupsLoading++;
      this.fieldService.getCadGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.CAD, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.CONFLUENCE)) {
      this.groupsLoading++;
      this.fieldService.getConfluenceGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.CONFLUENCE, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.FILESERVICE)) {
      this.groupsLoading++;
      this.fieldService.getFileserviceGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.FILESERVICE, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.GENERAL)) {
      this.groupsLoading++;
      this.fieldService.getGeneralGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.GENERAL, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.IT)) {
      this.groupsLoading++;
      this.fieldService.getItGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.IT, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.JIRA)) {
      this.groupsLoading++;
      this.fieldService.getJiraGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.JIRA, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.MCLOUD)) {
      this.groupsLoading++;
      this.fieldService.getMCloudGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.MCLOUD, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.MUSIC_MASTER)) {
      this.groupsLoading++;
      this.fieldService.getMusicMasterGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.MUSIC_MASTER, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.PROJECT)) {
      this.groupsLoading++;
      this.fieldService.getProjectGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.PROJECT, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.formHasType(FieldType.RABBIT)) {
      this.groupsLoading++;
      this.fieldService.getRabbitGroups().subscribe({
        next: (next) => {
          this.groups.set(FieldType.RABBIT, next);
          this.prepareGroupFields();
        },
        error: (err) => {
          this.browserMessagesService.showHttpError(err, 'Gruppen konnten nicht geladen werden');
          this.prepareGroupFields();
        }
      });
    }
    if (this.groupsLoading === 0) {
      this.groupsLoaded = true;
      this.getMyApprovers();
    }
  }

  getProductionUnits(): void {
    if (this.formHasType(FieldType.PRODUCTION_UNIT)) {
      this.fieldService.getProductionUnits().subscribe(
        (next) => {
          this.productionUnits = next;
        }
      );
    }
  }

  prepareGroupFields(): void {
    this.groupsLoading--;
    if (this.groupsLoading === 0) {
      if (this.process?.fields) {
        for (const field of this.process.fields) {
          if (field.formField?.type === 'APPLICATION' || field.formField?.type === 'BIMEDIAL' || field.formField?.type === 'CAD' || field.formField?.type === 'CONFLUENCE' || field.formField?.type === 'FILESERVICE' || field.formField?.type === 'GENERAL' || field.formField?.type === 'IT' || field.formField?.type === 'JIRA' || field.formField?.type === 'MCLOUD' || field.formField?.type === 'MUSIC_MASTER' || field.formField?.type === 'PROJECT' || field.formField?.type === 'RABBIT') {
            if (!field.formField.fieldCustomParameter.additional) {
              field.formField.fieldCustomParameter.additional = {};
            }
            for (const item of (this.groups.get(field.formField.type) ?? [])) {
              if (!field.formField.fieldCustomParameter.additional[item]) {
                field.formField.fieldCustomParameter.additional[item] = '';
              }
            }
          }
        }
      }
      this.groupsLoaded = true;
      this.getMyApprovers();
      this.fieldService.getGroupSuggestions().subscribe(data => this.groupSuggestions = data);
    }
  }

  getClients(): void {
    if (!this.formHasType(FieldType.CLIENT)) {
      return;
    }
    this.fieldService.getClients().subscribe(
      (next) => {
        this.clientList = next;
      }
    );
  }

  getSpeedikonRooms(): void {
    if (!this.formHasType(FieldType.ROOM)) {
      this.roomsLoaded = true;
      return;
    }
    this.fieldService.getSpeedikonRooms().subscribe({
      next: (next) => {
        this.rooms = next.map(value => ({ value }));
        this.roomsLoaded = true;
        this.getMyApprovers();
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Räume konnten nicht geladen werden');
        this.roomsLoaded = true;
        this.getMyApprovers();
      }
    });
  }

  getCostCentres(): void {
    if (!this.formHasType(FieldType.COST_CENTRE)) {
      this.costCentreLoaded = true;
      return;
    }
    this.fieldService.getCostCentres().subscribe({
      next: (next) => {
        this.costCentres = next;
        this.costCentreLoaded = true;
        this.getMyApprovers();
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Kostenstellen konnten nicht geladen werden');
        this.costCentreLoaded = true;
        this.getMyApprovers();
      }
    });
  }

  getMySpeedikonRooms(): void {
    if (!this.formHasType(FieldType.ROOM)) {
      this.myRooms = [];
      this.myRoomsLoaded = true;
      this.getMyApprovers();
      return;
    }
    this.fieldService.getMySpeedikonRooms().subscribe({
      next: (next) => {
        this.myRooms = next;
        this.myRoomsLoaded = true;
        this.getMyApprovers();
      },
      error: () => {
        this.myRooms = [];
        this.myRoomsLoaded = true;
        this.getMyApprovers();
      }
    });
  }

  getMyTelefonbuchData(): void {
    if (!this.formHasType(FieldType.TELEPHONE) && !this.formHasType(FieldType.MOBILE) && !this.formHasType(FieldType.FAX)) {
      this.telefonbuchData = new TelefonbuchData();
      this.telefonbuchDataLoaded = true;
      this.getMyApprovers();
      return;
    }
    this.fieldService.getMyTelefonbuchData().subscribe({
      next: (next) => {
        this.telefonbuchData = next;
        this.telefonbuchDataLoaded = true;
        this.getMyApprovers();
      },
      error: () => {
        this.telefonbuchData = new TelefonbuchData();
        this.telefonbuchDataLoaded = true;
        this.getMyApprovers();
      }
    });
  }

  orgUnitChanged(): void {
    this.employeeService.getMyApprovers().subscribe({
      next: (next) => {
        this.employee = next;
        if (this.process) {
          if (this.process.status === 'DRAFT' && this.process.fields) {
            for (const field of this.process.fields) {
              if (field.formField?.type === 'ORG_UNIT' || field.formField?.type === 'EMPLOYEE_NUMBER' || field.formField?.type === 'EMPLOYEE_TYPE' || field.formField?.type === 'MOBILE_WORK_AGREEMENT' || field.formField?.type === 'DISABILITY_STATUS') {
                this.setDefaultFieldContent(field);
                this.checkField(field);
              }
            }
          }
        }
      }
    });
    this.getMyApprovers();
  }

  reloadApprovers(field: ProcessField): void {
    if (field.formField?.fieldCustomParameter.forApproval) {
      this.getMyApprovers();
    }
  }

  searchPerson = (search: string, formFieldId: unknown): Observable<Person[]> => {
    return this.fieldService.searchPerson(search, formFieldId as string);
  };
  searchAccount = (search: string, formFieldId: unknown): Observable<string[]> => {
    return this.fieldService.searchAccount(search, formFieldId as string);
  };

  getMyApprovers(): void {
    if (!this.myRoomsLoaded || !this.telefonbuchDataLoaded || !this.roomsLoaded || !this.costCentreLoaded || !this.groupsLoaded) {
      return;
    }

    this.approvalsLoading = true;

    if (!this.employee) {
      this.employeeService.getMyApprovers().subscribe({
        next: (next) => {
          this.employee = next;
          if (this.process) {
            this.process.firstName = this.employee.firstName;
            this.process.lastName = this.employee.lastName;
            this.process.username = this.employee.username;
            this.process.emailAddress = this.employee.emailAddress;
            this.process.uidNumber = this.employee.uidNumber;
            if (this.process.fields && this.form?.fields) {
              const tmpProcessFields = [] as ProcessField[];
              for (const field of this.form.fields) {
                const tmpProcessField = new ProcessField();
                tmpProcessField.formField = field;
                this.setDefaultFieldContent(tmpProcessField);
                tmpProcessFields.push(tmpProcessField);
              }
              this.process.fields = tmpProcessFields;
              const existingProcess = this.existingProcess();
              if (existingProcess?.id) {
                this.restoreFromExistingProcess(existingProcess);
              }
              if (this.fillId) {
                this.fill();
              }
              if (this.params?.keys.length && !this.params.keys.includes('fillId')) {
                this.fillGetParameter();
              }
            }
            if (this.process.fields) {
              for (const field of this.process.fields) {
                if (field.formField?.type === 'ORG_UNIT' || field.formField?.type === 'EMPLOYEE_NUMBER' || field.formField?.type === 'EMPLOYEE_TYPE' || field.formField?.type === 'MOBILE_WORK_AGREEMENT' || field.formField?.type === 'DISABILITY_STATUS') {
                  this.setDefaultFieldContent(field);
                  this.checkField(field);
                }
              }
            }
          }
        }
      });
    }

    if (this.form?.id) {
      const costCentreField = this.process?.fields?.find(field => field.formField?.type === FieldType.COST_CENTRE && field.formField.fieldCustomParameter.forApproval) as ProcessField | undefined;
      const personField = this.process?.fields?.find(field => field.formField?.type === FieldType.PERSON && field.formField.fieldCustomParameter.forApproval) as ProcessField | undefined;
      this.formService.getFormApprovals(this.form.id, costCentreField?.content, personField?.formField?.fieldCustomParameter.select?.[0]?.username, personField?.formField?.fieldCustomParameter.select?.[0]?.uidNumber).subscribe({
        next: (next) => {
          if (this.process) {
            this.process.approvals = next;
            if (this.process.approvals && this.process.fields) {
              let approvalsValid = true;
              for (const approval of this.process.approvals) {
                if (!approval.username) {
                  if (approval.approverType === 'COST_CENTRE_RESPONSIBLE' || approval.approverType === 'COST_CENTRE_RESPONSIBLE_AND_DEPUTY') {
                    let forApproval = false;
                    for (const field of this.process.fields) {
                      if (field.formField?.type === 'COST_CENTRE' && field.formField.fieldCustomParameter.forApproval) {
                        forApproval = true;
                        break;
                      }
                    }
                    if (!forApproval) {
                      approvalsValid = false;
                      break;
                    }
                  } else if (approval.approverType === 'SELECTED_APPROVER') {
                    let forApproval = false;
                    for (const field of this.process.fields) {
                      if (field.formField?.type === 'PERSON' && field.formField.fieldCustomParameter.forApproval) {
                        forApproval = true;
                        break;
                      }
                    }
                    if (!forApproval) {
                      approvalsValid = false;
                      break;
                    }
                  } else if (approval.approverType !== 'APPROVER_GROUP') {
                    approvalsValid = false;
                  }
                }
              }
              this.approvalsValid = approvalsValid;
            }
          }
          this.approvalsLoading = false;
        },
        error: () => {
          this.approvalsValid = false;
          this.approvalsLoading = false;
        }
      });
    }
  }

  setDefaultFieldContent(processField: ProcessField): void {
    switch (processField.formField?.type) {
      case FieldType.YES_NO:
        processField.content = null;
        break;
      case FieldType.NUMBER:
        processField.content = processField.formField.fieldCustomParameter.min;
        break;
      case FieldType.TEXT:
        processField.content = '';
        break;
      case FieldType.DATE:
        processField.content = '';
        break;
      case FieldType.PERIOD:
        processField.content = '';
        processField.formField.fieldCustomParameter.fromDate = '';
        processField.formField.fieldCustomParameter.toDate = '';
        break;
      case FieldType.PERCENT:
        processField.content = '';
        processField.formField.fieldCustomParameter.absolute = undefined;
        processField.formField.fieldCustomParameter.percent = undefined;
        break;
      case FieldType.TIME:
        processField.content = '';
        break;
      case FieldType.CLIENT:
        processField.content = '';
        break;
      case FieldType.EMPLOYEE_TYPE:
        processField.content = this.employee?.employeeType ?? '';
        break;
      case FieldType.EMPLOYEE_NUMBER:
        processField.content = this.employee?.employeeNumber ?? '';
        break;
      case FieldType.MOBILE_WORK_AGREEMENT:
        processField.content = this.employee?.mobileWorkAgreement ?? 'keine';
        break;
      case FieldType.DISABILITY_STATUS:
        processField.content = this.employee?.disabilityStatus ? 'Ja' : 'Nein';
        break;
      case FieldType.EMAIL:
        processField.content = '';
        break;
      case FieldType.MOBILE:
        processField.content = this.telefonbuchData?.mobile ?? '';
        break;
      case FieldType.FAX:
        processField.content = this.telefonbuchData?.fax ?? '';
        break;
      case FieldType.TELEPHONE:
        processField.content = this.telefonbuchData?.telephone ?? '';
        break;
      case FieldType.ORG_UNIT:
        processField.content = this.employee?.colloquialPath ?? '';
        break;
      case FieldType.COST_CENTRE:
        if (processField.formField.fieldCustomParameter.myCostCentre) {
          processField.formField.fieldCustomParameter.select = this.costCentres.filter(costCentre => costCentre.costCentre === this.employee?.costCentre);
          processField.content = processField.formField.fieldCustomParameter.select[0]?.costCentre;
        } else {
          processField.formField.fieldCustomParameter.select = [];
          processField.content = null;
        }
        break;
      case FieldType.ROOM:
        processField.formField.fieldCustomParameter.select = processField.formField.fieldCustomParameter.myRoom ? this.rooms.filter(room => room.value === this.myRooms?.[0]) : [];
        processField.content = processField.formField.fieldCustomParameter.myRoom && this.myRooms?.[0] ? this.myRooms[0] : null;
        break;
      case FieldType.SELECTION:
        processField.content = [] as string[];
        this.contentSelect[processField.id ?? ''] = null;
        this.otherInputs[processField.id ?? ''] = '';
        break;
      case FieldType.SELECTION_PLUS:
        processField.content = [] as string[];
        processField.formField.fieldCustomParameter.additional = {};
        for (const item of processField.formField.fieldCustomParameter.items) {
          if (!processField.formField.fieldCustomParameter.additional[item]) {
            processField.formField.fieldCustomParameter.additional[item] = '';
          }
        }
        if (processField.formField.fieldCustomParameter.sum) {
          processField.content.push('Summe -> --' + (processField.formField.fieldCustomParameter.suffix ?? ''));
        }
        break;
      case FieldType.LIST:
        processField.content = [] as string[];
        for (let i = 0; i < (processField.formField.fieldCustomParameter.min ? processField.formField.fieldCustomParameter.min : 1); i++) {
          processField.content.push('');
        }
        break;
      case FieldType.PERIODS:
        processField.content = [] as string[];
        processField.formField.fieldCustomParameter.fromDate = [] as string[];
        processField.formField.fieldCustomParameter.toDate = [] as string[];
        for (let i = 0; i < (processField.formField.fieldCustomParameter.min ? processField.formField.fieldCustomParameter.min : 1); i++) {
          processField.content.push('');
          processField.formField.fieldCustomParameter.fromDate.push('');
          processField.formField.fieldCustomParameter.toDate.push('');
        }
        break;
      case FieldType.ACCOUNT:
        processField.content = '';
        processField.formField.fieldCustomParameter.select = [];
        break;
      case FieldType.PERSON:
        processField.content = '';
        processField.formField.fieldCustomParameter.select = [];
        break;
      case FieldType.PERSON_LIST:
      case FieldType.PRODUCTION_UNIT:
        processField.content = [];
        processField.formField.fieldCustomParameter.select = [];
        break;
      case FieldType.TABLE:
        processField.content = [] as string[];
        processField.content.push(processField.formField.fieldCustomParameter.head);
        for (let i = 0; i < (processField.formField.fieldCustomParameter.min ? processField.formField.fieldCustomParameter.min : 1); i++) {
          processField.content.push([]);
          // eslint-disable-next-line no-empty-pattern
          for (const {} of processField.formField.fieldCustomParameter.head) {
            processField.content[i + 1].push('');
          }
        }
        if (processField.formField?.fieldCustomParameter?.sum) {
          processField.content.push([]);
          // eslint-disable-next-line no-empty-pattern
          for (const {} of processField.formField.fieldCustomParameter.head) {
            processField.content[processField.content.length - 1].push('--');
          }
        }
        break;
      case FieldType.SELECTION_TABLE:
        processField.content = {};
        break;
      case FieldType.FILE:
        processField.content = [];
        break;
      case FieldType.APPLICATION:
      case FieldType.BIMEDIAL:
      case FieldType.CAD:
      case FieldType.CONFLUENCE:
      case FieldType.FILESERVICE:
      case FieldType.GENERAL:
      case FieldType.IT:
      case FieldType.JIRA:
      case FieldType.MCLOUD:
      case FieldType.MUSIC_MASTER:
      case FieldType.PROJECT:
      case FieldType.RABBIT:
        processField.content = [] as string[];
        processField.formField.fieldCustomParameter.additional = {};
        for (const item of (this.groups.get(processField.formField?.type) ?? [])) {
          if (!processField.formField.fieldCustomParameter.additional[item]) {
            processField.formField.fieldCustomParameter.additional[item] = '';
          }
        }
        break;
    }
  }

  getFieldErrors(): number {
    return this.process?.fields?.map(field => field.check).filter(check => check).length ?? 0;
  }

  checkField(field: ProcessField): void {
    field.check = '';

    if (!field.formField) {
      field.check = 'Interner Fehler.';
      return;
    }

    if (field.formField.type === FieldType.EMPLOYEE_NUMBER || field.formField.type === FieldType.EMPLOYEE_TYPE || field.formField.type === FieldType.ORG_UNIT || field.formField.type === FieldType.MOBILE_WORK_AGREEMENT || field.formField.type === FieldType.DISABILITY_STATUS) {
      this.setDefaultFieldContent(field);
    }

    if (this.process?.fields && !this.checkConditional(this.process.fields, this.process.fields.indexOf(field))) {
      return;
    }

    const hasNoContent = (!field.content && field.content !== 0)
      || (Array.isArray(field.content) && field.content.filter(s => Array.isArray(s) ? s.filter(t => t).length : s).length === (field.formField.type === 'TABLE' ? (field.formField.fieldCustomParameter.sum ? 2 : 1) : 0))
      || (typeof field.content === 'object' && !Object.keys(field.content).length);

    if (hasNoContent && field.formField.required && field.formField.type !== FieldType.HEADER && field.formField.type !== FieldType.SEPARATOR_LINE && field.formField.type !== FieldType.DESCRIPTION && field.formField.type !== FieldType.BLANK_SPACE) {
      field.check = 'Dies ist ein Pflichtfeld.';
      this.disableNav();
      return;
    }

    if ((field.formField.type === FieldType.EMPLOYEE_NUMBER || field.formField.type === FieldType.EMPLOYEE_TYPE || field.formField.type === FieldType.ORG_UNIT || field.formField.type === FieldType.MOBILE_WORK_AGREEMENT || field.formField.type === FieldType.DISABILITY_STATUS) && hasNoContent) {
      field.check = 'Ihre Mitarbeiterdaten konnten nicht geladen werden. Bitte öffnen Sie das Formular erneut.';
      this.disableNav();
      return;
    }

    if (!hasNoContent) {
      switch (field.formField.type) {
        case FieldType.TEXT:
          if (field.formField.fieldCustomParameter.minLength && field.content.length < field.formField.fieldCustomParameter.minLength) {
            field.check = field.formField.fieldCustomParameter.minLength === field.formField.fieldCustomParameter.maxLength ? 'Es müssen genau ' + field.formField.fieldCustomParameter.minLength + ' Zeichen eingegeben werden.' : 'Es müssen mindesten ' + field.formField.fieldCustomParameter.minLength + ' Zeichen eingegeben werden.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.maxLength && field.content.length > field.formField.fieldCustomParameter.maxLength) {
            field.check = field.formField.fieldCustomParameter.minLength === field.formField.fieldCustomParameter.maxLength ? 'Es müssen genau ' + field.formField.fieldCustomParameter.maxLength + ' Zeichen eingegeben werden.' : 'Es dürfen maximal ' + field.formField.fieldCustomParameter.maxLength + ' Zeichen eingegeben werden.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.regex) {
            const regex = new RegExp(field.formField.fieldCustomParameter.regex);
            if (!field.content.match(regex)) {
              field.check = field.formField.fieldCustomParameter.regexErrorMessage;
              this.disableNav();
              return;
            }
          }
          break;
        case FieldType.NUMBER: {
          const n = Number(field.content);
          if (isNaN(n)) {
            field.check = 'Dies ist keine Zahl.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.integer && Math.floor(field.content) !== n) {
            field.check = 'Es dürfen keine Kommastellen eingegeben werden.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.min && n < field.formField.fieldCustomParameter.min) {
            field.check = 'Die Mindestgröße ist ' + field.formField.fieldCustomParameter.min + '.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.max && n > field.formField.fieldCustomParameter.max) {
            field.check = 'Die Maximalgröße ist ' + field.formField.fieldCustomParameter.max + '.';
            this.disableNav();
            return;
          }
          break;
        }
        case FieldType.TABLE: {
          if (field.content.length < 1 || field.content[0].length !== field.formField.fieldCustomParameter.head.length) {
            field.check = 'Der Tabellenkopf hat eine unterschiedliche Anzahl an Spalten.';
            this.disableNav();
            return;
          }
          for (let i = 0; i < field.formField.fieldCustomParameter.head.length; i++) {
            if (field.content[0][i] !== field.formField.fieldCustomParameter.head[i]) {
              field.check = 'Der Tabellenkopf enthält abweichend Spaltennamen.';
              this.disableNav();
              return;
            }
          }

          const tableLength = field.content.filter((s: string[]) => s.filter((t: string) => t).length).length;
          if (field.formField.fieldCustomParameter.min && tableLength < field.formField.fieldCustomParameter.min + (field.formField.fieldCustomParameter.sum ? 2 : 1)) {
            field.check = 'Es müssen mindestens ' + field.formField.fieldCustomParameter.min + ' Reihen eingetragen werden.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.max && tableLength > field.formField.fieldCustomParameter.max + (field.formField.fieldCustomParameter.sum ? 2 : 1)) {
            field.check = 'Es dürfen maximal ' + field.formField.fieldCustomParameter.max + ' Reihen eingetragen werden.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.sum && field.content[field.content.length - 1].filter((s: string) => s !== '--' && !s.startsWith('Σ ')).length) {
            field.check = 'Die Summenreihe ist fehlerhaft.';
            this.disableNav();
            return;
          }
          break;
        }
        case FieldType.SELECTION_TABLE:
          if (Object.keys(field.content).filter(item => !field.formField?.fieldCustomParameter.items.includes(item)).length) {
            field.check = 'Interner Fehler: Es sind Einträge enthalten, die nicht auswählbar sind.';
            this.disableNav();
            return;
          }
          if (field.formField.required && (Object.keys(field.content).length !== field.formField?.fieldCustomParameter.items.length || Object.keys(field.content).filter(item => !field.content[item]?.length).length)) {
            field.check = 'Es muss bei jedem Eintrag etwas ausgewählt werden.';
            this.disableNav();
            return;
          }
          if (Object.keys(field.content).filter(item => field.content[item].filter((option: string) => !field.formField?.fieldCustomParameter.options.includes(option)).length).length) {
            field.check = 'Interner Fehler: Es sind Optionen ausgewählt, die nicht auswählbar sind.';
            this.disableNav();
            return;
          }
          if (!field.formField?.fieldCustomParameter.multipleFieldPerOptions && Object.keys(field.content).filter(item => field.content[item].length > 1).length) {
            field.check = 'Interner Fehler: Es sind zu viele Optionen ausgewählt.';
            this.disableNav();
            return;
          }
          break;
        case FieldType.SELECTION: {
          const other = field.formField.fieldCustomParameter.other;
          let otherUsed = false;
          for (const s of field.content) {
            if (!field.formField.fieldCustomParameter.items.includes(s)) {
              if (other && !otherUsed && s.startsWith('Sonstiges: ')) {
                otherUsed = true;
              } else {
                field.check = '"' + s + '" ist eine ungültige Auswahl.';
                this.disableNav();
                return;
              }
            }
          }
          const selectionLength = field.content.filter((s: string) => s).length;
          if (field.formField.fieldCustomParameter.minSelection && selectionLength < field.formField.fieldCustomParameter.minSelection) {
            field.check = 'Es müssen mindestens ' + field.formField.fieldCustomParameter.minSelection + ' Möglichkeiten ausgewählt werden.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.maxSelection && selectionLength > field.formField.fieldCustomParameter.maxSelection) {
            field.check = 'Es dürfen maximal ' + field.formField.fieldCustomParameter.maxSelection + ' Möglichkeiten ausgewählt werden.';
            this.disableNav();
            return;
          }
          break;
        }
        case FieldType.SELECTION_PLUS: {
          const selectionPlusItems = field.content.filter((s: string) => s);
          if (field.formField.required && field.formField.fieldCustomParameter.sum && selectionPlusItems.length <= 1) {
            field.check = 'Dies ist ein Pflichtfeld.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.minSelection && selectionPlusItems.length - (field.formField.fieldCustomParameter.sum ? 1 : 0) < field.formField.fieldCustomParameter.minSelection) {
            field.check = 'Es müssen mindestens ' + field.formField.fieldCustomParameter.minSelection + ' Möglichkeiten ausgewählt werden.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.maxSelection && selectionPlusItems.length - (field.formField.fieldCustomParameter.sum ? 1 : 0) > field.formField.fieldCustomParameter.maxSelection) {
            field.check = 'Es dürfen maximal ' + field.formField.fieldCustomParameter.maxSelection + ' Möglichkeiten ausgewählt werden.';
            this.disableNav();
            return;
          }
          let type = '';
          for (const item of selectionPlusItems) {
            const parts = item.split(/( -> )/, 2);
            let value = item.replace(parts[0] + ' -> ', '');
            if (field.formField.fieldCustomParameter.suffix) {
              if (value.trim() && !value.endsWith(field.formField.fieldCustomParameter.suffix)) {
                field.check = '"' + parts[0] + '" endet nicht mit ' + field.formField.fieldCustomParameter.suffix + '.';
                this.disableNav();
                return;
              }
              value = value.replace(new RegExp(field.formField.fieldCustomParameter.suffix + '$'), '');
            }
            if (field.formField.fieldCustomParameter.sum && parts[0] === 'Summe') {
              continue;
            }
            if (!field.formField.fieldCustomParameter.items.includes(parts[0])) {
              field.check = '"' + parts[0] + '" ist eine ungültige Auswahl.';
              this.disableNav();
              return;
            }
            if (field.formField.fieldCustomParameter.forceAdditionalField && (parts.length < 2 || !value.trim())) {
              field.check = field.formField.fieldCustomParameter.items.length < 2 ? 'Das Feld muss befüllt werden.' : 'Es müssen für "' + parts[0] + '" zusätzliche Informationen angegeben werden.';
              this.disableNav();
              return;
            }
            if (field.formField.fieldCustomParameter.forceAdditionalField && field.formField.fieldCustomParameter.sum) {
              const normalizedValue = value.replace(/\./g, '').replace(/,/g, '.');
              if (type === '') {
                if (!isNaN(normalizedValue)) {
                  type = 'number';
                  continue;
                }
                if (normalizedValue.match(/^\d+(:[0-5]\d)+$/g)) {
                  type = 'time';
                  continue;
                }
              }
              if (type === 'number') {
                if (!isNaN(normalizedValue)) {
                  continue;
                }
              }
              if (type === 'time') {
                if (normalizedValue.match(/^\d+(:[0-5]\d)+$/g)) {
                  continue;
                }
              }
              field.check = 'Es dürfen nur Zahlen oder Zeiten (hh:mm, mm:ss oder hh:mm:ss) eingetragen werden.';
              this.disableNav();
              return;
            }
          }
          break;
        }
        case FieldType.APPLICATION:
        case FieldType.BIMEDIAL:
        case FieldType.CAD:
        case FieldType.CONFLUENCE:
        case FieldType.FILESERVICE:
        case FieldType.GENERAL:
        case FieldType.IT:
        case FieldType.JIRA:
        case FieldType.MCLOUD:
        case FieldType.MUSIC_MASTER:
        case FieldType.PROJECT:
        case FieldType.RABBIT: {
          const groupItems = field.content.filter((s: string) => s);
          for (const item of groupItems) {
            const parts = item.split(/( -> )/, 2);
            const value = item.replace(parts[0] + ' -> ', '');
            if (!this.groups.get(field.formField.type)?.includes(parts[0])) {
              field.check = '"' + parts[0] + '" ist eine ungültige Auswahl.';
              this.disableNav();
              return;
            }
            if (parts.length < 2 || !value.trim()) {
              field.check = 'Bitte ergänzen Sie zu Ihrer Auswahl "' + parts[0] + '" eine Beschreibung und/oder Begründung oben in das dazugehörige Feld.';
              this.disableNav();
              return;
            }
          }
          break;
        }
        case FieldType.LIST: {
          const listLength = field.content.filter((s: string) => s).length;
          if (field.formField.fieldCustomParameter.min && listLength < field.formField.fieldCustomParameter.min) {
            field.check = 'Es müssen mindestens ' + field.formField.fieldCustomParameter.min + ' Einträge vorhanden sein.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.max && listLength > field.formField.fieldCustomParameter.max) {
            field.check = 'Es dürfen maximal ' + field.formField.fieldCustomParameter.max + ' Einträge vorhanden sein.';
            this.disableNav();
            return;
          }
          break;
        }
        case FieldType.PERIODS: {
          const periodsLength = field.content.filter((s: string) => s).length;
          if (field.formField.fieldCustomParameter.min && periodsLength < field.formField.fieldCustomParameter.min) {
            field.check = 'Es müssen mindestens ' + field.formField.fieldCustomParameter.min + ' Einträge vorhanden sein.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.max && periodsLength > field.formField.fieldCustomParameter.max) {
            field.check = 'Es dürfen maximal ' + field.formField.fieldCustomParameter.max + ' Einträge vorhanden sein.';
            this.disableNav();
            return;
          }
          for (const value of field.content.filter((s: string) => s)) {
            if (!value) {
              field.check = 'Das Feld muss befüllt werden.';
              this.disableNav();
              return;
            }
            const content = value.split(' ');
            if (content.length !== 7 || content[0] !== 'Von' || content[2] !== 'bis' || content[4] !== '->' || content[6] !== 'Tage') {
              field.check = 'Die Struktur des Inhalts ist ungültig.';
              this.disableNav();
              return;
            }
            const fromParts = content[1].split('.');
            const from = new Date(Number(fromParts[2]), Number(fromParts[1]) - 1, Number(fromParts[0]));
            if (content[1] === '' || !isFinite(from.getTime()) || from > new Date('9999-12-31')) {
              field.check = 'Das Von-Datum muss befüllt werden und darf maximal der 31.12.9999 sein.';
              this.disableNav();
              return;
            }
            const toParts = content[3].split('.');
            const to = new Date(Number(toParts[2]), Number(toParts[1]) - 1, Number(toParts[0]));
            if (content[3] === '' || !isFinite(from.getTime()) || to > new Date('9999-12-31')) {
              field.check = 'Das Bis-Datum muss befüllt werden und darf maximal der 31.12.9999 sein.';
              this.disableNav();
              return;
            }
            if (from > to) {
              field.check = 'Das Bis-Datum darf nicht vor dem Von-Datum liegen.';
              this.disableNav();
              return;
            }
            const days = Number(content[5]);
            if (days !== Math.round((to.getTime() - from.getTime()) / 86400000) + 1) {
              field.check = 'Die berechneten Tage sind nicht korrekt.';
              this.disableNav();
              return;
            }
            if (field.formField.fieldCustomParameter.sameMonth && uniq(field.content.filter((s: string) => s).map((s: string) => s.substring(7, 14))).length > 1) {
              field.check = 'Die Startdaten müssen alle im selben Monat liegen.';
              this.disableNav();
              return;
            }
          }
          for (let i = 0; i < field.content.filter((s: string) => s).length; i++) {
            for (let j = i + 1; j < field.content.filter((s: string) => s).length; j++) {
              if (field.formField.fieldCustomParameter.fromDate[i] <= field.formField.fieldCustomParameter.toDate[j]
                && field.formField.fieldCustomParameter.toDate[i] >= field.formField.fieldCustomParameter.fromDate[j]) {
                field.check = 'Es gibt überlappende Zeiträume.';
                this.disableNav();
                return;
              }
            }
          }
          break;
        }
        case FieldType.FILE: {
          const filesLength = field.content.filter((s: string) => s).length;
          if (field.formField.fieldCustomParameter.min && filesLength < field.formField.fieldCustomParameter.min) {
            field.check = 'Es müssen mindestens ' + field.formField.fieldCustomParameter.min + ' Dateien hochgeladen werden.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.max && filesLength > field.formField.fieldCustomParameter.max) {
            field.check = 'Es dürfen maximal ' + field.formField.fieldCustomParameter.max + ' Dateien hochgeladen werden.';
            this.disableNav();
            return;
          }
          break;
        }
        case FieldType.EMAIL:
          if (!field.content.match(/^[a-zA-Z\d\/.!#$%&’*+=?^_`{|}~-]+@[a-zA-Z\d-]+(?:\.[a-zA-Z\d-]+)+$/g)) {
            field.check = 'Es muss eine gültige E-Mail-Adresse eingegeben werden.';
            this.disableNav();
            return;
          }
          break;
        case FieldType.DATE: {
          const d = new Date(field.content);
          if (!isFinite(d.getTime()) || d > new Date('9999-12-31')) {
            field.check = 'Das Datum darf maximal der 31.12.9999 sein.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.future && d <= new Date(new Date().toISOString().substring(0, 10))) {
            field.check = 'Das Datum muss in der Zukunft liegen.';
            this.disableNav();
            return;
          }
          break;
        }
        case FieldType.PERIOD: {
          const content = field.content.split(' ');
          if (content.length !== 4 || content[0] !== 'Von' || content[2] !== 'bis') {
            field.check = 'Die Struktur des Inhalts ist ungültig.';
            this.disableNav();
            return;
          }
          const fromParts = content[1].split('.');
          const from = new Date(Number(fromParts[2]), Number(fromParts[1]) - 1, Number(fromParts[0]));
          if (!isFinite(from.getTime()) || from > new Date('9999-12-31')) {
            field.check = 'Das Von-Datum muss befüllt werden und darf maximal der 31.12.9999 sein.';
            this.disableNav();
            return;
          }
          if (!field.formField.fieldCustomParameter.unlimited || content[3] !== 'unbefristet') {
            const toParts = content[3].split('.');
            const to = new Date(Number(toParts[2]), Number(toParts[1]) - 1, Number(toParts[0]));
            if (!isFinite(to.getTime()) || to > new Date('9999-12-31')) {
              field.check = 'Das Bis-Datum muss befüllt werden und darf maximal der 31.12.9999 sein.';
              this.disableNav();
              return;
            }
            if (from > to) {
              field.check = 'Das Bis-Datum darf nicht vor dem Von-Datum liegen.';
              this.disableNav();
              return;
            }
          }
          break;
        }
        case FieldType.PERCENT: {
          const parts = field.content.replace('von ' + field.formField.fieldCustomParameter.absoluteHundred.toString().replace('.', ',') + (field.formField.fieldCustomParameter.suffix ?? '') + ' entspricht ', '')
            .replace('%', '')
            .split(' ');
          if (parts.length !== 2) {
            field.check = 'Die Struktur des Inhalts ist ungültig.';
            this.disableNav();
            return;
          }
          let absolute = parts[0];
          let absoluteHundred = field.formField?.fieldCustomParameter.absoluteHundred;
          if (field.formField.fieldCustomParameter.timeFormat) {
            if (absolute.match(/^[0-9]+:([0-5][0-9])$/)) {
              const absoluteSplit = absolute.split(':');
              absolute = Number.parseInt(absoluteSplit[0], 10) * 60;
              absolute += Number.parseInt(absoluteSplit[1], 10);
            } else {
              absolute = 0;
            }
            const absoluteHundredSplit = absoluteHundred.split(':');
            absoluteHundred = Number.parseInt(absoluteHundredSplit[0], 10) * 60;
            absoluteHundred += Number.parseInt(absoluteHundredSplit[1], 10);
          } else {
            absolute = Number(parts[0].replace(',', '.'));
          }
          const percent = Number(parts[1].replace(',', '.'));

          if (Math.round(absolute * 10000 / absoluteHundred) / 100 !== percent && Math.round(percent * absoluteHundred) / 100 !== absolute && Math.round(percent * absoluteHundred / 100) !== absolute) {
            field.check = 'Absolut- und Prozentwert sind unstimmig.';
            this.disableNav();
            return;
          }
          if (percent > 100 && !field.formField.fieldCustomParameter.moreThanHundred) {
            field.check = 'Es sind maximal 100% erlaubt.';
            this.disableNav();
            return;
          }
          if (percent < 0 && !field.formField.fieldCustomParameter.lassThanNull) {
            field.check = 'Es sind mindestens 0% nötig.';
            this.disableNav();
            return;
          }
          break;
        }
        case FieldType.PERSON_LIST: {
          const personListLength = field.content.filter((s: string) => s).length;
          if (field.formField.fieldCustomParameter.min && personListLength < field.formField.fieldCustomParameter.min) {
            field.check = 'Es müssen mindestens ' + field.formField.fieldCustomParameter.min + ' Einträge vorhanden sein.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.max && personListLength > field.formField.fieldCustomParameter.max) {
            field.check = 'Es dürfen maximal ' + field.formField.fieldCustomParameter.max + ' Einträge vorhanden sein.';
            this.disableNav();
            return;
          }
          break;
        }
        case FieldType.PRODUCTION_UNIT: {
          const productionUnitLength = field.content.filter((s: string) => s).length;
          if (field.formField.fieldCustomParameter.min && productionUnitLength < field.formField.fieldCustomParameter.min) {
            field.check = 'Es müssen mindestens ' + field.formField.fieldCustomParameter.min + ' Einträge vorhanden sein.';
            this.disableNav();
            return;
          }
          if (field.formField.fieldCustomParameter.max && productionUnitLength > field.formField.fieldCustomParameter.max) {
            field.check = 'Es dürfen maximal ' + field.formField.fieldCustomParameter.max + ' Einträge vorhanden sein.';
            this.disableNav();
            return;
          }
          break;
        }
        case FieldType.MOBILE_WORK_AGREEMENT:
          if (field.formField.fieldCustomParameter.required && field.content === 'keine') {
            field.check = 'Um das Formular abschicken zu dürfen, muss eine aktuelle gültige Vereinbarung für mobiles Arbeiten in SAP vorliegen.';
            this.disableNav();
            return;
          }
          break;
        case FieldType.COST_CENTRE:
          if (!this.costCentres.map(costCentre => costCentre.costCentre).includes(field.content)) {
            field.check = field.content + ' ist keine gültige Kostenstelle.';
            this.disableNav();
            return;
          }
          break;
      }
    }
  }

  checkAllFields(): void {
    this.checkAll = true;
    if (this.process?.fields) {
      for (const field of this.process.fields) {
        this.checkField(field);
        if (field.check) {
          this.checkAll = false;
        }
      }
    }
  }

  disableNav(): void {
    this.checkAll = false;
    this.steps[1].disabled = true;
    this.steps[2].disabled = true;
  }

  checkFieldAndForward(): void {
    this.checkAllFields();
    if (this.checkAll) {
      this.steps[1].disabled = false;
      this.steps[2].disabled = !this.checkPrivacy;
      this.changeCurrentStep(this.currentStep + 1);
    } else {
      this.steps[1].disabled = true;
      this.steps[2].disabled = true;
    }
  }

  checkFormAndSend(): void {
    if (this.checkAll && this.checkPrivacy && this.process) {
      this.saving = true;
      this.savingError = '';
      this.openSaveModal();
      const processStatus = this.process.status;
      this.process.status = ProcessStatus.SENT;
      const observer = {
        next: (): void => {
          this.closeSaveModal();
          this.saving = false;
          this.router.navigate(['/my-forms']).then();
          this.browserMessagesService.addMessage(new BrowserMessage(
            'Antrag gesendet',
            'Der Antrag wurde erfolgreich gesendet',
            BrowserMessageType.Success
          ));
        },
        // eslint-disable-next-line
        error: (error: any): void => {
          if (this.process) {
            this.process.status = processStatus;
          }
          this.saving = false;
          this.savingError = error.error?.localizedDescription ? this.utilService.removeStyleMarkup(error.error.localizedDescription) : 'Fehlercode: ' + error.status;
        }
      };
      if (this.process.id) {
        this.processService.updateProcess(new BodyProcess(this.prepareFinalProcess(this.process)!), this.process.id, false).subscribe(observer);
      } else {
        this.processService.createProcess(new BodyProcess(this.prepareFinalProcess(this.process)!), false).subscribe(observer);
      }
    }
  }

  createDraftUrl(): void {
    if (this.process?.fields) {
      this.draftUrl = undefined;
      const url = new URL(this.webUrl + '/tag/' + this.form?.tag);

      for (const field of this.process.fields) {
        const content = new BodyProcessField(field).content;
        if (content && field.formField?.name && field.formField.type !== 'FILE') {
          url.searchParams.append(field.formField.name, content);
        }
        this.draftUrl = url.href;
      }
    }
  }

  saveFormAsDraft(notify: boolean): void {
    if (this.process) {
      this.saving = true;
      this.savingError = '';
      this.openSaveModal();
      const processStatus = this.process.status;
      this.process.status = ProcessStatus.DRAFT;
      const observer = {
        next: (): void => {
          this.closeSaveModal();
          this.saving = false;
          this.router.navigate(['/my-forms']).then();
          this.browserMessagesService.addMessage(new BrowserMessage(
            'Entwurf gespeichert',
            'Der Entwurf wurde erfolgreich gespeichert',
            BrowserMessageType.Success
          ));
        },
        // eslint-disable-next-line
        error: (error: any): void => {
          if (this.process) {
            this.process.status = processStatus;
          }
          this.saving = false;
          this.savingError = error.error?.localizedDescription ? this.utilService.removeStyleMarkup(error.error.localizedDescription) : 'Fehlercode: ' + error.status;
        }
      };
      if (this.process.id) {
        this.processService.updateProcess(new BodyProcess(this.prepareFinalProcess(this.process)!), this.process.id, notify).subscribe(observer);
      } else {
        this.processService.createProcess(new BodyProcess(this.prepareFinalProcess(this.process)!), notify).subscribe(observer);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  checkboxController(field: ProcessField, option: string, event: any): void {
    if (event.currentTarget.checked) {
      if (!field.content.includes(option)) {
        field.content.push(option);
      }
    } else {
      const index: number = field.content.indexOf(option);
      if (index !== -1) {
        field.content.splice(index, 1);
      }
    }
    field.content = field.content.sort((a: string, b: string) => {
      let x = field.formField?.fieldCustomParameter?.items?.indexOf(a.split(/( -> )/, 2)[0]);
      let y = field.formField?.fieldCustomParameter?.items?.indexOf(b.split(/( -> )/, 2)[0]);
      if (x === -1) {
        x = field.formField?.fieldCustomParameter?.items.length;
      }
      if (y === -1) {
        y = field.formField?.fieldCustomParameter?.items.length;
      }
      return x - y;
    });
  }

  mapEmployeeType(employeeType: EmployeeType): string {
    switch (employeeType) {
      case EmployeeType.PERMANENT:
        return 'Fest';
      case EmployeeType.FREELANCER:
        return 'Frei';
      case EmployeeType.TEMPORARY:
        return 'Aushilfe';
      case EmployeeType.EXTERNAL:
        return 'Extern';
    }
  }

  processStyleMarkupWithRequired(s: string | null | undefined): string | null | undefined {
    return this.processStyleMarkupWithoutBreaks(s) + '<sup class="text-primary">*</sup>';
  }

  processStyleMarkupWithoutBreaks(s: string | null | undefined): string | null | undefined {
    return s ? this.utilService.processStyleMarkupWithoutBreaks(s) : '';
  }

  processStyleMarkup(s: string | null | undefined): string | null | undefined {
    return s ? this.utilService.processStyleMarkup(s) : '';
  }

  getAllowedFileTypes(field: ProcessField | undefined): string[] {
    const types = [] as string[];
    if (field?.formField) {
      for (const mimeType of field.formField.fieldCustomParameter.fileTypes) {
        switch (mimeType) {
          case 'image/png':
            types.push('.png');
            break;
          case 'image/jpeg':
            types.push('.jpeg');
            types.push('.jpg');
            types.push('.jpe');
            break;
          case 'application/pdf':
            types.push('.pdf');
            break;
          case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            types.push('.docx');
            break;
          case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            types.push('.xlsx');
            break;
          case 'application/acad':
            types.push('.dwg');
            break;
          case 'application/vnd.visio':
            types.push('.vsd');
            break;
          case 'application/vnd.visio2013':
            types.push('.vsdx');
            break;
        }
      }
    }
    return types;
  }

  openFileModal(field: ProcessField): void {
    const ngFileModal = this.ngFileModal();
    if (ngFileModal) {
      this.fileField = field;
      this.fileContent = undefined;
      this.checking = false;
      this.checkingError = '';
      this.ngFileModalService = this.modalService.open(ngFileModal, { centered: true, backdrop: 'static' });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fileSelected(event: any): void {
    this.fileContent = {};
    this.fileContent.name = event.target.files[0].name.normalize('NFC');
    this.fileContent.mimeType = event.target.files[0].type;
    // eslint-disable-next-line no-console
    console.log(this.fileContent.name);
    // eslint-disable-next-line no-console
    console.log(this.fileContent.mimeType);
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    reader.onload = (): void => {
      if (reader.result) {
        const altDwgMimeTypes = [
          'application/acad',
          'application/x-acad',
          'application/autocad_dwg',
          'image/x-dwg',
          'application/dwg',
          'application/x-dwg',
          'application/x-autocad',
          'image/vnd.dwg',
          'drawing/dwg'
        ];
        const altCommonVisioMimeTypes = [
          'application/vnd.visio',
          'application/vnd.visio2013',
          'application/vnd.ms-visio',
          'application/vnd.ms-visio.viewer',
          'application/vnd.ms-visio.drawing'
        ];
        if (this.fileContent) {
          if ((!this.fileContent.mimeType || altDwgMimeTypes.includes(this.fileContent.mimeType)) && this.fileContent.name?.endsWith('.dwg')) {
            this.fileContent.mimeType = 'application/acad';
          }
          if ((!this.fileContent.mimeType || altCommonVisioMimeTypes.includes(this.fileContent.mimeType)) && this.fileContent.name?.endsWith('.vsd')) {
            this.fileContent.mimeType = 'application/vnd.visio';
          }
          if ((!this.fileContent.mimeType || altCommonVisioMimeTypes.includes(this.fileContent.mimeType)) && this.fileContent.name?.endsWith('.vsdx')) {
            this.fileContent.mimeType = 'application/vnd.visio2013';
          }
          this.fileContent.data = reader.result.toString();
          const i = this.fileContent.data.indexOf('base64,');
          this.fileContent.data = this.fileContent.data.substring(i + 7);
          if (this.fileContent.data.length > 13981013) {
            this.checkingError = 'Die Datei darf maximal 10 MB groß sein.';
          } else if (!this.fileField?.formField?.fieldCustomParameter.fileTypes.includes(this.fileContent.mimeType)) {
            this.checkingError = 'Der Dateityp ist nicht erlaubt.';
          } else {
            this.checkFile();
          }
        }
      }
    };
  }

  checkFile(): void {
    this.checkingError = '';
    this.checking = true;
    this.fieldService.checkFile(new BodyContent(JSON.stringify([this.fileContent]))).subscribe({
      next: () => {
        this.checking = false;
        this.closeFileModal();
        if (this.fileField) {
          this.fileField.content.push(this.fileContent);
          this.checkField(this.fileField);
        }
      },
      error: (error) => {
        this.checkingError = this.utilService.removeStyleMarkup(error.error.localizedDescription.replace(' für "check"', ''));
        this.checking = false;
        if (this.fileField) {
          this.checkField(this.fileField);
        }
      }
    });
  }

  closeFileModal(): void {
    this.ngFileModalService?.close();
  }

  removeFile(field: ProcessField, i: number): void {
    field.content.splice(i, 1);
    if (this.fileField) {
      this.checkField(this.fileField);
    }
  }

  formHasType(type: FieldType): boolean {
    if (this.form?.fields) {
      for (const field of this.form.fields) {
        if (field.type === type) {
          return true;
        }
      }
    }
    return false;
  }

  openSaveModal(): void {
    const ngSaveModal = this.ngSaveModal();
    if (ngSaveModal) {
      this.ngSaveModalService = this.modalService.open(ngSaveModal, { centered: true, backdrop: 'static' });
    }
  }

  closeSaveModal(): void {
    this.ngSaveModalService?.close();
  }

  updatePeriod(field: ProcessField): void {
    if (field.formField?.fieldCustomParameter.fromDate || field.formField?.fieldCustomParameter.toDate) {
      field.content = 'Von ';
      if (field.formField.fieldCustomParameter.fromDate) {
        const fromDate = new Date(field.formField.fieldCustomParameter.fromDate);
        field.content += fromDate.toISOString().substring(8, 10) + '.' + fromDate.toISOString().substring(5, 7) + '.' + fromDate.toISOString().substring(0, 4);
      }
      field.content += ' bis ';
      if (field.formField.fieldCustomParameter.toDate) {
        if (field.formField.fieldCustomParameter.toDate === 'unbefristet') {
          field.content += 'unbefristet';
        } else {
          const toDate = new Date(field.formField.fieldCustomParameter.toDate);
          field.content += toDate.toISOString().substring(8, 10) + '.' + toDate.toISOString().substring(5, 7) + '.' + toDate.toISOString().substring(0, 4);
        }
      }
    } else {
      field.content = '';
    }
  }

  updatePercent(field: ProcessField, updateAbsolute = false, updatePercent = false): void {
    if (field.formField) {
      if (updatePercent) {
        if (field.formField?.fieldCustomParameter.absolute === null || field.formField?.fieldCustomParameter.absolute === '') {
          field.formField.fieldCustomParameter.percent = null;
        } else {
          let absolute = field.formField.fieldCustomParameter.absolute;
          let absoluteHundred = field.formField.fieldCustomParameter.absoluteHundred;
          if (field.formField.fieldCustomParameter.timeFormat) {
            if (absolute.match(/^[0-9]+:([0-5][0-9])$/)) {
              const absoluteSplit = absolute.split(':');
              absolute = Number.parseInt(absoluteSplit[0], 10) * 60;
              absolute += Number.parseInt(absoluteSplit[1], 10);
            } else {
              absolute = 0;
            }
            const absoluteHundredSplit = absoluteHundred.split(':');
            absoluteHundred = Number.parseInt(absoluteHundredSplit[0], 10) * 60;
            absoluteHundred += Number.parseInt(absoluteHundredSplit[1], 10);
          }
          field.formField.fieldCustomParameter.percent = Math.round(absolute * 10000 / absoluteHundred) / 100;
        }
      }
      if (updateAbsolute) {
        if (field.formField?.fieldCustomParameter.percent === null) {
          field.formField.fieldCustomParameter.absolute = null;
        } else {
          let absoluteHundred = field.formField.fieldCustomParameter.absoluteHundred;
          if (field.formField.fieldCustomParameter.timeFormat) {
            const absoluteHundredSplit = absoluteHundred.split(':');
            absoluteHundred = Number.parseInt(absoluteHundredSplit[0], 10) * 60;
            absoluteHundred += Number.parseInt(absoluteHundredSplit[1], 10);
          }
          let absolute: (number | string) = Math.round(field.formField.fieldCustomParameter.percent * absoluteHundred) / 100;
          if (field.formField.fieldCustomParameter.timeFormat) {
            absolute = Math.floor(absolute / 60) + ':' + Math.round(absolute % 60).toString().padStart(2, '0');
          }
          field.formField.fieldCustomParameter.absolute = absolute;
        }
      }
      if ((field.formField?.fieldCustomParameter.absolute !== undefined && field.formField?.fieldCustomParameter.absolute !== null) || (field.formField?.fieldCustomParameter.percent !== undefined && field.formField?.fieldCustomParameter.percent !== null)) {
        field.content = field.formField?.fieldCustomParameter.absolute.toString().replace('.', ',');
        field.content += ' von ' + field.formField?.fieldCustomParameter.absoluteHundred.toString().replace('.', ',');
        if (field.formField.fieldCustomParameter.suffix) {
          field.content += field.formField.fieldCustomParameter.suffix;
        }
        field.content += ' entspricht ' + field.formField?.fieldCustomParameter.percent.toString().replace('.', ',') + '%';
      } else {
        field.content = '';
      }
    }
  }

  updateOtherSelection(field: ProcessField): void {
    for (let i = 0; i < field.content.length; i++) {
      if (!field.formField?.fieldCustomParameter.items.includes(field.content[i])) {
        field.content[i] = 'Sonstiges: ' + this.otherInputs[field.id ?? ''];
        break;
      }
    }
  }

  changeRadioSelection(field: ProcessField, content: string | null): void {
    field.content = content ? [content] : [];
  }

  changeRadioSelectionPlus(field: ProcessField, item: string | null): void {
    let content = [] as string[];
    if (item) {
      content = [item + ' -> ' + field.formField?.fieldCustomParameter.additional[item] + (field.formField?.fieldCustomParameter.suffix ?? '')];
    }
    field.content = field.formField?.fieldCustomParameter?.sum ? content.concat(['Summe -> --' + (field.formField.fieldCustomParameter.suffix ?? '')]) : content;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  checkboxControllerPlus(field: ProcessField, item: string, event: any): void {
    if (event.currentTarget.checked) {
      for (const i of field.content) {
        const parts = i.split(/( -> )/, 2);
        if (parts[0] === item) {
          return;
        }
      }
      field.content.push(item + ' -> ' + field.formField?.fieldCustomParameter.additional[item] + (field.formField?.fieldCustomParameter.suffix ?? ''));
    } else {
      for (let i = 0; i < field.content.length; i++) {
        const parts = field.content[i].split(/( -> )/, 2);
        if (parts[0] === item) {
          field.content.splice(i, 1);
          break;
        }
      }
    }
    field.content = field.content.sort((a: string, b: string) => {
      let x = field.formField?.fieldCustomParameter?.items?.indexOf(a.split(/( -> )/, 2)[0]);
      let y = field.formField?.fieldCustomParameter?.items?.indexOf(b.split(/( -> )/, 2)[0]);
      if (x === -1) {
        x = field.formField?.fieldCustomParameter?.items.length;
      }
      if (y === -1) {
        y = field.formField?.fieldCustomParameter?.items.length;
      }
      return x - y;
    });
  }

  updateSelectionPlus(field: ProcessField, item: string): void {
    for (let i = 0; i < field.content.length; i++) {
      const parts = field.content[i].split(/( -> )/, 2);
      if (parts[0] === item) {
        field.content[i] = item + ' -> ' + field.formField?.fieldCustomParameter.additional[item] + (field.formField?.fieldCustomParameter.suffix ?? '');
        break;
      }
    }
  }

  changeSelectionTable(field: ProcessField, item: string, option: string): void {
    if (field.content[item]?.includes(option)) {
      field.content[item].splice(field.content[item].findIndex((s: string) => s === option), 0);
      if (!field.content[item].length) {
        field.content[item] = undefined;
      }
    } else {
      if (!field.content[item]) {
        field.content[item] = [];
      }
      field.content[item].push(option);
    }
  }

  getHead(field: ProcessField): string[] {
    return field.formField?.fieldCustomParameter?.head ?? [];
  }

  getItems(field: ProcessField): string[] {
    const items = field.formField?.fieldCustomParameter?.items ?? [];
    return field.formField?.fieldCustomParameter?.sum ? items.concat(['Summe']) : items;
  }

  getOptions(field: ProcessField): string[] {
    return field.formField?.fieldCustomParameter?.options ?? [];
  }

  defaultHeader(): boolean {
    return !!this.process?.fields && this.process.fields.length > 0 && this.process.fields[0].formField?.type !== 'HEADER';
  }

  sumSelectionPlus(field: ProcessField): string {
    if (!field.formField?.fieldCustomParameter?.sum) {
      if (!field.content?.fieldCustomParameter?.items?.includes('Summe')) {
        field.content = field.content.filter((s: string) => !s.startsWith('Summe -> '));
      }
      return '--';
    }

    let response = '--';
    const suffix = field.formField?.fieldCustomParameter?.suffix ?? '';
    const values = field.content.filter((s: string) => !s.startsWith('Summe -> ')).map((s: string) => s.replace(s.split(/( -> )/, 2)[0] + ' -> ', '').replace(new RegExp(suffix + '$'), '')) as string[];

    if (values[0]?.includes(':')) {
      const normalizedValues = values.filter((value: string | undefined) => value).map(value => value.trim()).filter(value => value.match(/^\d+(:[0-5]\d)+$/g));
      if (normalizedValues.length) {
        response = this.sumNormalizedTimesAsStrings(normalizedValues);
      }
    } else {
      const normalizedValues = values.filter((value: string | undefined) => value).map(value => value.replace(/\./g, '').replace(/,/g, '.').trim())
        .filter(value => !isNaN(Number(value)));
      if (normalizedValues.length) {
        response = this.sumNormalizedNumbersAsStrings(normalizedValues).replace('.', ',');
      }
    }

    response += suffix;

    let found = false;
    for (let i = 0; i < field.content.length; i++) {
      const parts = field.content[i].split(/( -> )/, 2);
      if (parts[0] === 'Summe') {
        found = true;
        field.content[i] = 'Summe -> ' + response;
        break;
      }
    }
    if (!found) {
      field.content.push('Summe -> ' + response);
    }
    return response;
  }

  sumTableColumn(field: ProcessField, column: number): string {
    if (!field.formField?.fieldCustomParameter?.sum) {
      return '--';
    }

    let response = '--';
    const normalizedNumbers = field.content.map((value: string[]) => value[column]).filter((value: string | undefined) => value).map((value: string) => value.replace(/\./g, '').replace(/,/g, '.').trim())
      .filter((value: string) => value && !isNaN(Number(value)));

    if (normalizedNumbers.length) {
      const sum = this.sumNormalizedNumbersAsStrings(normalizedNumbers);
      response = 'Σ ' + sum.replace('.', ',');
    } else {
      const normalizedTimes = field.content.map((value: string[]) => value[column]).filter((value: string | undefined) => value).map((value: string) => value.trim()).filter((value: string) => value.match(/^\d+(:[0-5]\d)+$/g));
      if (normalizedTimes.length) {
        response = 'Σ ' + this.sumNormalizedTimesAsStrings(normalizedTimes);
      }
    }
    field.content[field.content.length - 1][column] = response;
    return response;
  }

  sumNormalizedNumbersAsStrings(normalizedValues: string[]): string {
    const precisionDigits = Math.max(0, ...normalizedValues.filter(value => value.includes('.')).map(value => value.split('.')[1]).filter(value => value).map(value => value.length));
    const preparedValues = cloneDeep(normalizedValues).map((value) => {
      if (!precisionDigits) {
        return value;
      }
      const length = value.split('.')[1]?.length ?? 0;
      return value.replace('.', '') + '0'.repeat(precisionDigits - length);
    });

    let sum = 0;
    for (const value of preparedValues) {
      const n = Number(value);
      sum += n;
    }
    if (precisionDigits) {
      const s = sum.toString();
      return s.substring(0, s.length - precisionDigits) + '.' + s.substring(s.length - precisionDigits);
    }
    return sum.toString();
  }

  sumNormalizedTimesAsStrings(normalizedValues: string[]): string {
    const timeParts = [] as number[];
    let response = '';
    for (const value of normalizedValues) {
      const rowTimeParts = value.split(':').map(s => Number(s)).reverse();
      for (let j = 0; j < rowTimeParts.length; j++) {
        if (!timeParts[j]) {
          timeParts[j] = 0;
        }
        timeParts[j] += rowTimeParts[j];
      }
    }
    if (!timeParts.filter(num => num).length) {
      return '--';
    }
    for (let j = 0; j < timeParts.length - 1; j++) {
      const carryover = Math.floor(timeParts[j] / 60);
      timeParts[j] %= 60;
      timeParts[j + 1] += carryover;
    }
    for (let j = timeParts.length - 1; j >= 0; j--) {
      if (j !== timeParts.length - 1) {
        response += ':';
        if (timeParts[j] < 10) {
          response += '0';
        }
      }
      response += timeParts[j];
    }

    return response;
  }

  updateCostCentre(field: ProcessField): void {
    if (field.formField?.fieldCustomParameter.select?.[0]) {
      field.content = field.formField.fieldCustomParameter.select[0].costCentre;
    } else {
      field.content = null;
    }
  }

  updateRooms(field: ProcessField): void {
    if (field.formField?.fieldCustomParameter.select?.[0]) {
      field.content = field.formField.fieldCustomParameter.select[0].value;
    } else {
      field.content = null;
    }
  }

  updatePerson(field: ProcessField): void {
    if (field.formField?.fieldCustomParameter.select?.[0]) {
      const person = field.formField.fieldCustomParameter.select[0];
      field.content = 'Vorname: ' + person.firstName;
      field.content += '\nNachname: ' + person.lastName;
      field.content += '\nBenutzername: ' + person.username;
      field.content += '\nE-Mail-Adresse: ' + person.emailAddress;
      field.content += '\nUID: ' + person.uidNumber;
    } else {
      field.content = '';
    }
  }

  updateAccount(field: ProcessField): void {
    field.content = field.formField?.fieldCustomParameter.select?.join('\n') ?? '';
  }

  updatePersonList(field: ProcessField): void {
    field.content = field.formField?.fieldCustomParameter.select?.map((person: Person) => {
      let personString = 'Vorname: ' + person.firstName;
      personString += '\nNachname: ' + person.lastName;
      personString += '\nBenutzername: ' + person.username;
      personString += '\nE-Mail-Adresse: ' + person.emailAddress;
      personString += '\nUID: ' + person.uidNumber;
      return personString;
    }) ?? [];
  }

  updateProductionUnit(field: ProcessField): void {
    field.content = field.formField?.fieldCustomParameter.select?.map((productionUnit: { Bezeichnung?: string; SYSID?: string }) => {
      let productionUnitString = 'Bezeichnung: ' + productionUnit.Bezeichnung;
      productionUnitString += '\nSYSID: ' + productionUnit.SYSID;
      return productionUnitString;
    }) ?? [];
  }

  getVisibleGroupOptions(field: ProcessField, expanded: boolean): string[] {
    let groups = field.formField?.type ? this.groups.get(field.formField.type) : [] as string[];
    groups = groups ?? [];
    return expanded ? groups : groups.filter(option => field.content?.includes(option + ' -> ' + field.formField?.fieldCustomParameter?.additional[option]));
  }

  openGroupModal(field: ProcessField): void {
    const groupModal = this.groupModal();
    if (groupModal) {
      this.selectedGroupField = field;
      this.groupSearch = '';
      this.modalService.open(groupModal, { centered: true, backdrop: 'static' }).result.then(() => this.selectedGroupField = undefined);
    }
  }

  checkAcceptedPrivacy(): void {
    this.formService.getFormsWithAlreadyAcceptedPrivacy().subscribe(
      (data) => {
        if (this.id && data.includes(this.id.toLowerCase())) {
          this.checkPrivacy = true;
        }
      }
    );
  }

  fieldChanged(field: Field): boolean {
    const fields = this.lastApproved()?.fields ?? this.form?.lastApproved?.fields;
    if (!fields) {
      return false;
    }
    if (!this.lastApproved() && (field.type === FieldType.HEADER || field.type === FieldType.SEPARATOR_LINE || field.type === FieldType.DESCRIPTION || field.type === FieldType.BLANK_SPACE)) {
      return false;
    }
    for (const lastField of fields) {
      if (field.name === lastField.name
        && field.description === lastField.description
        && field.required === lastField.required
        && field.type === lastField.type
        && field.definition === lastField.definition
        && isEqual(field.fieldCustomParameter, lastField.fieldCustomParameter)) {
        return false;
      }
    }
    return true;
  }

  calculateDays(field: ProcessField, i: number): number {
    if (!field.formField?.fieldCustomParameter.fromDate[i] || field.formField?.fieldCustomParameter.fromDate[i] === '') {
      return 0;
    }
    if (!field.formField?.fieldCustomParameter.toDate[i] || field.formField?.fieldCustomParameter.toDate[i] === '') {
      return 0;
    }
    const from = new Date(field.formField?.fieldCustomParameter.fromDate[i]);
    if (!isFinite(from.getTime()) || from > new Date('9999-12-31')) {
      return 0;
    }
    const to = new Date(field.formField?.fieldCustomParameter.toDate[i]);
    if (!isFinite(from.getTime()) || to > new Date('9999-12-31')) {
      return 0;
    }
    if (from > to) {
      return 0;
    }
    return Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
  }

  updatePeriodsContent(field: ProcessField, i: number): void {
    if ((!field.formField?.fieldCustomParameter.fromDate[i])
      && (!field.formField?.fieldCustomParameter.toDate[i])) {
      field.content[i] = '';
      return;
    }
    let content = 'Von ';
    if (field.formField?.fieldCustomParameter.fromDate[i]) {
      const fromDate = new Date(field.formField.fieldCustomParameter.fromDate[i]);
      content += fromDate.toISOString().substring(8, 10) + '.' + fromDate.toISOString().substring(5, 7) + '.' + fromDate.toISOString().substring(0, 4);
    }
    content += ' bis ';
    if (field.formField?.fieldCustomParameter.toDate[i]) {
      const toDate = new Date(field.formField.fieldCustomParameter.toDate[i]);
      content += toDate.toISOString().substring(8, 10) + '.' + toDate.toISOString().substring(5, 7) + '.' + toDate.toISOString().substring(0, 4);
    }
    content += ' -> ';
    content += this.calculateDays(field, i);
    content += ' Tage';
    field.content[i] = content;
  }

  groupFormatSplit(value: string): string[] {
    let start = 0;
    const indexes = [0] as number[];
    const i = value.toLowerCase().indexOf(this.groupSearch);
    if (i !== -1) {
      start += i;
      indexes.push(start);
      start += this.groupSearch.length;
      indexes.push(start);
    }
    indexes.push(value.length);
    const values = [] as string[];
    for (let j = 0; j < indexes.length - 1; j++) {
      values.push(value.substring(indexes[j], indexes[j + 1]));
    }
    return values;
  }

  checkConditional(fields: ProcessField[], index: number): boolean {
    if (!fields[index].formField?.conditionalType) {
      return true;
    }

    const conditionalTerms = [] as string[];
    for (let i = 0; i < index; i++) {
      const field = fields[i];
      if (!field.formField?.tag) {
        continue;
      }
      switch (field.formField?.conditionalType) {
        case ConditionalType.AND:
          if (field.formField?.conditionalTerms?.filter(term => !conditionalTerms.includes(term)).length) {
            continue;
          }
          break;
        case ConditionalType.OR:
          if (!field.formField?.conditionalTerms?.filter(term => conditionalTerms.includes(term)).length) {
            continue;
          }
          break;
      }
      switch (field.formField.type) {
        case FieldType.ACCOUNT:
          if (field.content) {
            field.content.split('\n')
              .map((s: string) => field.formField?.tag + '=' + s.split(/[()]/g).filter((s: string) => s).reverse()[0])
              .forEach((s: string) => conditionalTerms.push(s));
          }
          break;
        case FieldType.DISABILITY_STATUS:
        case FieldType.YES_NO:
          if (field.content) {
            conditionalTerms.push(field.formField.tag + '=' + field.content);
          }
          break;
        case FieldType.EMPLOYEE_TYPE:
          if (field.content) {
            conditionalTerms.push(field.formField.tag + '=' + this.mapEmployeeType(field.content));
          }
          break;
        case FieldType.MOBILE_WORK_AGREEMENT:
          if (field.content) {
            if (field.content === 'keine') {
              conditionalTerms.push(field.formField.tag + '=Nein');
            } else {
              conditionalTerms.push(field.formField.tag + '=Ja');
            }
          }
          break;
        case FieldType.SELECTION:
          conditionalTerms.push(...field.content.map((item: string) => field.formField?.tag + '=' + item));
          break;
      }
    }

    switch (fields[index].formField?.conditionalType) {
      case ConditionalType.AND:
        return !fields[index].formField?.conditionalTerms?.filter(term => !conditionalTerms.includes(term)).length;
      case ConditionalType.OR:
        return !!fields[index].formField?.conditionalTerms?.filter(term => conditionalTerms.includes(term)).length;
    }

    return false;
  }

  prepareFinalProcess(process: Process | undefined): Process | undefined {
    process = cloneDeep(process);
    if (process?.fields) {
      process.fields = process.fields.filter(field => this.checkConditional(process.fields!, process.fields!.indexOf(field)));
    }
    return process;
  }

  groupFieldPlaceholder(field: ProcessField): string {
    switch (field.formField?.type) {
      case FieldType.CONFLUENCE:
        return 'Lese-, Schreib- oder Adminrechte für diesen Bereich und warum?';
      case FieldType.JIRA:
        return 'Lese-, Schreib- oder Adminrechte für diesen Bereich und warum?';
    }
    return 'Bitte die ausgewählte Anforderung bzw. Rolle hier beschreiben und/oder begründen.';
  }

  protected readonly EmployeeType = EmployeeType;
  protected readonly OrgUnitType = OrgUnitType;
  protected readonly FieldType = FieldType;
  protected readonly ProcessStatus = ProcessStatus;
}
